import { ApiError } from '@/api/client';
import type { Locale, TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { RevisionDiff } from '@/api/types';

/**
 * Request revisions (ADR-089) as words: what changed in a request after it was sent to the warehouse, and why a
 * change was refused. The server sends codes and values; every sentence lives in the dictionaries (EN + RU).
 */

const LINE_KEYS: Record<'ADDED' | 'REMOVED' | 'CHANGED', TranslationKey> = {
  ADDED: 'revise.line.ADDED',
  REMOVED: 'revise.line.REMOVED',
  CHANGED: 'revise.line.CHANGED',
};

const FIELD_KEYS: Record<string, TranslationKey> = {
  reference: 'revise.f.reference',
  expectedAt: 'revise.f.expectedAt',
  dueAt: 'revise.f.dueAt',
  'shipTo.name': 'revise.f.shipTo.name',
  'shipTo.phone': 'revise.f.shipTo.phone',
  'shipTo.email': 'revise.f.shipTo.email',
  'shipTo.address': 'revise.f.shipTo.address',
  'shipTo.city': 'revise.f.shipTo.city',
  'shipTo.postalCode': 'revise.f.shipTo.postalCode',
  'shipTo.country': 'revise.f.shipTo.country',
  'shipTo.comment': 'revise.f.shipTo.comment',
};
const DATE_FIELDS = new Set(['expectedAt', 'dueAt']);

/** One sentence per change, e.g. "SKU-1: quantity 20 → 25". */
export function summarizeDiff(t: TranslateFn, locale: Locale, diff: RevisionDiff | null | undefined): string[] {
  if (!diff) return [];
  const out: string[] = [];
  for (const l of diff.lines ?? []) {
    out.push(t(LINE_KEYS[l.op], { sku: l.skuCode, from: formatNumber(l.from ?? 0, locale), to: formatNumber(l.to ?? 0, locale) }));
  }
  for (const f of diff.fields ?? []) {
    const show = (v: string | null): string => (v === null || v === '' ? '—' : DATE_FIELDS.has(f.field) ? formatDateTime(v, locale) : v);
    const label = FIELD_KEYS[f.field];
    out.push(t('revise.fieldChange', { field: label ? t(label) : f.field, from: show(f.from), to: show(f.to) }));
  }
  return out;
}

export type RevisionConflictItem = { skuId: string; skuCode: string; reason: string; received: string; requested: string };
const CONFLICT_KEYS: Record<string, TranslationKey> = {
  BELOW_RECEIVED: 'revise.conflict.BELOW_RECEIVED',
  REMOVE_RECEIVED: 'revise.conflict.REMOVE_RECEIVED',
  SPLIT_LINES: 'revise.conflict.SPLIT_LINES',
  OVERAGE_PENDING: 'revise.conflict.OVERAGE_PENDING',
  PICKING_STARTED: 'revise.conflict.PICKING_STARTED',
  ALREADY_PICKED: 'revise.conflict.ALREADY_PICKED',
};

/** The per-line conflicts of a refused revision, or null when the error is something else. */
export function revisionConflicts(err: unknown): RevisionConflictItem[] | null {
  if (!(err instanceof ApiError) || err.code !== 'SELLER_REQUEST.REVISION_CONFLICT') return null;
  try {
    const parsed: unknown = JSON.parse(String((err.params as Record<string, unknown>)['conflicts'] ?? '[]'));
    return Array.isArray(parsed) ? (parsed as RevisionConflictItem[]) : [];
  } catch {
    return [];
  }
}

export function describeConflict(t: TranslateFn, c: RevisionConflictItem): string {
  return t(CONFLICT_KEYS[c.reason] ?? 'revise.conflict.BELOW_RECEIVED', { sku: c.skuCode, received: c.received, requested: c.requested });
}
